/**
 * cinematicControl.js — ОДИН путь включения кинематографического рендеринга.
 *
 * ПОВОД, и он состоит из двух жалоб врача подряд.
 *
 * 1. «У меня две панели вызова рендеринга, зачем они обе?» В 10.19.14 я убрал
 *    дублирование параметров, но код оставил разный: кнопка «Рендер» в правой
 *    панели шла своим путём (создать рендерер, применить настройки, дождаться
 *    кадра), а кнопки «Кино вкл/выкл» слева и в правом сайдбаре — своим, через
 *    `cinematicToggle`. Разные пути = разное поведение, и это выяснилось сразу же.
 *
 * 2. «Нажатие на эти кнопки ни к чему не приводит, эти кнопки — пустышки».
 *    Измерено на живой сборке: кнопка печатала «Кинематографический рендеринг
 *    включён», а `isEnabled()` через полторы секунды оставался **false**. То есть
 *    плашка врала. Ровно то, против чего написан весь остальной код.
 *
 * 3. И главное. Врач прислал снимок: после «Рендер» вместо анатомии — чёрное поле
 *    с красными точками. Замер на живой сборке: у окна 34,9% непустых пикселей и
 *    средняя яркость 26, а у кинематографического слоя поверх него — **1,2%
 *    непустых и яркость 0,4**. Приёмка этого не видела, потому что проверяла
 *    размер кадра и счётчик кадров: кадр собирался, счётчик рос, «всё зелено».
 *
 * ПОЭТОМУ ЗДЕСЬ ТРИ ВЕЩИ:
 *
 *   · единственная функция включения, которой пользуются все кнопки;
 *   · ожидание НАСТОЯЩЕГО кадра (счётчик кадров рендерера обязан вырасти);
 *   · ЗАМЕР КАДРА: кинематографический слой сравнивается с окном под ним, и если
 *     он заметно темнее и пустее — слой снимается, а врачу говорится числами,
 *     что произошло. Показать чёрный кадр с красными точками вместо анатомии
 *     хуже, чем не показать ничего.
 */

import { ensureCinematicRenderer, set3DPreset, применитьМРПеренос } from '../core/viewport.js';
import { getCinematicRenderer, getRenderingEngine, getViewportIds } from '../core/viewportState.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { замерХолста } from '../rendering/frameMeasure.js';

/** «Качество» → длинная сторона кадра. Больше размера окна не бывает (см. рендерер). */
export const КАЧЕСТВО_В_ПИКСЕЛИ = { preview: 256, draft: 512, high: 1024, ultra: 2048 };

/** «Освещение» → расстановка лампʼ рендерера. */
export const СВЕТ = {
  studio:   [{ position: [3, 3, 4],  intensity: 1.0, color: [1, 0.96, 0.9],  radius: 14 },
             { position: [-4, 1, 2], intensity: 0.35, color: [0.85, 0.9, 1], radius: 18 }],
  surgical: [{ position: [0, 4, 1],  intensity: 1.6, color: [1, 1, 1],       radius: 8 }],
  dramatic: [{ position: [5, 2, 1],  intensity: 1.4, color: [1, 0.9, 0.75],  radius: 6 }],
  outdoor:  [{ position: [2, 6, 3],  intensity: 1.1, color: [1, 0.97, 0.88], radius: 20 },
             { position: [-3, 4, -2], intensity: 0.5, color: [0.7, 0.82, 1], radius: 25 }],
};

/** «Перенос»: КТ — таблицей пресетов, МРТ — по гистограмме серии. */
export const ПЕРЕНОС_КТ = { ct_default: 'ct-overview', ct_bone: 'ct-bone-clean', ct_vessels: 'ct-vascular' };
export const ПЕРЕНОС_МР = { 'mr-brain': 'МР мозг', 'mr-vessels': 'МР сосуды TOF', 'mr-overview': 'МР обзор' };

/** Порог замера: слой считается непригодным, если он настолько пустее окна. */
export const ПОРОГ_ПУСТОТЫ = 0.4;      // непустых пикселей меньше 40% от окна
export const ПОРОГ_ЯРКОСТИ = 0.3;      // средняя яркость меньше 30% от окна

/** Холст объёмного окна, поверх которого рисует кинематографический слой. */
function холстОкна() {
  try {
    const re = getRenderingEngine();
    const id = (getViewportIds() || []).find((i) => String(re?.getViewport(i)?.type || '').toLowerCase().includes('3d'));
    const vp = id ? re.getViewport(id) : null;
    return vp?.canvas || vp?.element?.querySelector('canvas') || null;
  } catch (e) { return null; }
}

/**
 * Пригоден ли кадр. Сравнение именно с окном, а не с абсолютным порогом: у
 * тёмного пресета окно тоже тёмное, и абсолютный порог ругался бы на норму.
 *
 * [10.19.17] ДВА НЕЗАВИСИМЫХ ЗАМЕРА. Андрей Вячеславович прислал числа со своей
 * машины (RTX 4060, WebGPU): «слой 0,0% непустых пикселей и яркость 0,0 против
 * 36,6% и 29,3 у окна». Ноль в обоих числах — это либо действительно чёрный
 * кадр, либо неудавшийся снимок холста, и старый код эти случаи НЕ РАЗЛИЧАЛ:
 * печатал нули и снимал слой. Снять с экрана работающий рендер и подтвердить
 * это числами — та же ложь, только с цифрами.
 *
 * Теперь: снимок 2D-канваса И чтение пикселей самим GPU. Авторитетным считается
 * замер с GPU. Если он не получился — слой НЕ снимается, а причина называется.
 *
 * @returns {Promise<{пригоден: boolean, повод: string, причина: string|null,
 *   зСлоя: object|null, зОкна: object|null, зСнимок: object|null, зGPU: object|null}>}
 */
export async function проверитьКадр(cr) {
  const слой = cr?.getCanvas?.() || null;
  const окно = холстОкна();
  const зОкна = замерХолста(окно);
  const зСнимок = замерХолста(слой);

  let зGPU = null;
  try {
    if (typeof cr?.замерКадраGPU === 'function') зGPU = await cr.замерКадраGPU(2500);
  } catch (e) { log.debug('[Кино/замерGPU]', e.message); }

  // Авторитетен замер с GPU: он читает то, что нарисовано, а не снимок холста.
  const зСлоя = (зGPU && зGPU.ок) ? зGPU : зСнимок;
  const общее = { зСлоя: зСлоя.ок ? зСлоя : null, зОкна: зОкна.ок ? зОкна : null, зСнимок, зGPU };

  if (!зОкна.ок) {
    return { пригоден: true, повод: 'окно-не-замерено', причина: `окно замерить не удалось (${зОкна.причина}) — слой оставлен`, ...общее };
  }
  if (!зСлоя.ок) {
    // ЗДЕСЬ БЫЛО САМОЕ ВАЖНОЕ ИСПРАВЛЕНИЕ: неудача замера — не приговор кадру.
    return { пригоден: true, повод: 'слой-не-замерен', причина: `замерить слой не удалось (${зСлоя.причина}) — слой оставлен, судить не по чему`, ...общее };
  }
  // Окно само пустое (исследование не открыто) — сравнивать не с чем.
  if (зОкна.доляНепустых < 0.02) {
    return { пригоден: true, повод: 'окно-пустое', причина: 'в окне и так почти нет содержимого', ...общее };
  }

  const пустее = зСлоя.доляНепустых < зОкна.доляНепустых * ПОРОГ_ПУСТОТЫ;
  const темнее = зСлоя.яркость < зОкна.яркость * ПОРОГ_ЯРКОСТИ;
  const числа = `слой ${(зСлоя.доляНепустых * 100).toFixed(1)}% непустых пикселей и яркость ${зСлоя.яркость.toFixed(1)} `
    + `против ${(зОкна.доляНепустых * 100).toFixed(1)}% и ${зОкна.яркость.toFixed(1)} у окна`
    + (зСлоя.источник ? ` (замер: ${зСлоя.источник})` : '');

  // Расхождение путей само по себе — находка: значит виноват снимок, не кадр.
  const расхождение = !!(зGPU?.ок && зСнимок.ок
    && зСнимок.доляНепустых < 0.02 && зGPU.доляНепустых >= 0.02);
  if (расхождение) {
    log.warn('[Кино/замер] снимок холста показал пустоту, а чтение с GPU — картинку. '
      + `Снимок: ${(зСнимок.доляНепустых * 100).toFixed(1)}%, GPU: ${(зGPU.доляНепустых * 100).toFixed(1)}%.`);
  }

  if (пустее || темнее) {
    return { пригоден: false, повод: 'кадр-непригоден', причина: числа, расхождение, ...общее };
  }
  return { пригоден: true, повод: 'кадр-годен', причина: null, расхождение, ...общее };
}

/**
 * [10.19.17] Замер С ПОВТОРОМ. Один пустой отсчёт — ещё не приговор.
 *
 * ПОЧЕМУ. Размер кадра меняется на ходу: ограничение качества при включении и
 * динамическое понижение разрешения (DRS) при просадке кадров в секунду. Смена
 * размера холста его ОЧИЩАЕТ, и до следующей отрисовки холст пуст — законно
 * пуст. Замер, попавший в этот промежуток, видит 0,0% и 0,0 яркости и снимает
 * с экрана исправно работающий слой. В журнале Андрея Вячеславовича DRS как раз
 * и работал: «[Cinematic/DRS] Снижение: масштаб 0.90 (FPS: 19)».
 *
 * Поэтому: непригодность подтверждается ВТОРЫМ замером, после нового кадра и
 * при неизменившемся размере холста. Числа обоих замеров уходят в журнал —
 * чтобы было видно, был ли это промежуток или кадр правда пуст.
 */
export async function проверитьКадрТвёрдо(cr, { повторов = 2, пауза = 500 } = {}) {
  let последний = null;
  for (let i = 0; i < Math.max(1, повторов); i++) {
    if (i > 0) {
      const было = размерХолста(cr);
      try { cr.forceRender?.(); } catch (e) { log.debug('[Кино/замер]', e.message); }
      await new Promise((r) => setTimeout(r, пауза));
      const стало = размерХолста(cr);
      if (было !== стало) log.warn(`[Кино/замер] размер холста менялся между замерами: ${было} → ${стало}`);
    }
    последний = await проверитьКадр(cr);
    последний.попытка = i + 1;
    if (последний.пригоден) return последний;
    log.warn(`[Кино/замер] попытка ${i + 1}: ${последний.причина}`);
  }
  return последний;
}

function размерХолста(cr) {
  const х = cr?.getCanvas?.();
  return х ? `${х.width}×${х.height}` : 'нет';
}

/**
 * [10.19.17] ДИАГНОСТИКА КОНВЕЙЕРА ПО ПРОХОДАМ.
 *
 * ПОВОД. Числа из плашки замера («слой 0,0% и яркость 0,0») говорят, ЧТО кадр
 * негоден, и ничего не говорят, ОТ ЧЕГО. У меня в песочнице только WebGL2, у
 * Андрея Вячеславовича — WebGPU на RTX 4060, и одна и та же поломка на них
 * выглядит по-разному. Гадать по описанию — потеря времени обоих.
 *
 * Поэтому: эффекты выключаются все, потом включаются ПО ОДНОМУ, и после каждого
 * кадр замеряется. Строка, на которой числа обваливаются, и есть виновный
 * проход. Диагностика запускается сама, когда замер снял слой, и печатается в
 * консоль — тем же способом, каким Андрей Вячеславович присылает мне журналы.
 *
 * @returns {Promise<Array<{проход: string, доляНепустых: number|null, яркость: number|null, откуда: string, причина: string|null}>>}
 */
export async function диагностикаКонвейера(cr = getCinematicRenderer()) {
  if (!cr || !cr.isEnabled?.()) {
    log.warn('[Кино/диагностика] рендерер не запущен — диагностировать нечего');
    return [];
  }
  const было = (() => { try { return cr.getParams?.(); } catch { return null; } })();

  const ВЫКЛ = {
    ssao: { enabled: false }, bloom: { enabled: false }, godRays: { enabled: false }, fxaa: { enabled: false },
    dof: { intensity: 0 }, edge: { intensity: 0 }, chromAb: 0, grain: 0, vignette: { intensity: 0 }, sharpen: 0,
    volumetric: { enabled: false }, ssgi: { enabled: false }, motionBlur: { enabled: false }, bokeh: { enabled: false },
    lut: { mode: 0, intensity: 0 },
    toneMap: { mode: 0, exposure: 1, gamma: 2.2 },
    colorGrade: { saturation: 1, contrast: 1, tint: [1, 1, 1], temperature: 0 },
  };
  const ШАГИ = [
    ['всё выключено', {}],
    ['тональное отображение', { toneMap: { mode: 1, exposure: 1.3, gamma: 2.2 } }],
    ['цветокор', { colorGrade: { saturation: 1.1, contrast: 1.4, tint: [1, 0.98, 0.95], temperature: 0.2 } }],
    ['сглаживание', { fxaa: { enabled: true } }],
    ['резкость', { sharpen: 0.4 }],
    ['виньетка', { vignette: { intensity: 0.35, radius: 0.85 } }],
    ['затенение', { ssao: { enabled: true, radius: 8, intensity: 0.6, bias: 0.01, samples: 24 } }],
    ['свечение', { bloom: { enabled: true, threshold: 0.65, softKnee: 0.3, intensity: 0.25, passes: 5 } }],
    ['лучи света', { godRays: { enabled: true, intensity: 0.3, density: 0.8, decay: 0.95, weight: 0.3 } }],
    ['глубина резкости', { dof: { intensity: 0.3, focus: 0.5, range: 0.35 } }],
    ['контур', { edge: { intensity: 0.15, color: [0.4, 0.7, 1] } }],
    ['хром. аберрация', { chromAb: 0.15 }],
    ['зерно', { grain: 0.2 }],
    ['плёночный профиль', { lut: { mode: 5, intensity: 0.8 } }],
  ];

  const строки = [];
  for (const [имя, добавка] of ШАГИ) {
    try { cr.setParams({ ...ВЫКЛ, ...добавка }, { trackHistory: false }); } catch (e) { log.debug('[Кино/диагностика]', e.message); }
    try { cr.forceRender?.(); } catch (e) { log.debug('[Кино/диагностика]', e.message); }
    await new Promise((r) => setTimeout(r, 350));
    const з = await проверитьКадр(cr);
    const л = з.зСлоя;
    строки.push({
      проход: имя,
      доляНепустых: л ? +(л.доляНепустых * 100).toFixed(1) : null,
      яркость: л ? +л.яркость.toFixed(1) : null,
      rgb: л?.rgb || null,
      откуда: л?.источник || (з.зGPU?.ок ? 'gpu' : 'снимок'),
      причина: л ? null : (з.причина || 'замер не удался'),
    });
  }

  // Настройки возвращаются как были: диагностика не должна менять картинку.
  if (было) { try { cr.setParams(было, { trackHistory: false }); cr.forceRender?.(); } catch (e) { log.debug('[Кино/диагностика]', e.message); } }

  const окно = замерХолста(холстОкна());
  console.log('[Кино/диагностика] движок: ' + (cr.getBackendName?.() || '?')
    + ', буфер глубины: ' + (cr.hasDepth?.() ? 'есть' : 'нет')
    + (окно.ок ? `, окно: ${(окно.доляНепустых * 100).toFixed(1)}% непустых, яркость ${окно.яркость.toFixed(1)}` : `, окно замерить не удалось: ${окно.причина}`));
  if (typeof console.table === 'function') console.table(строки);
  else строки.forEach((с) => console.log(`  ${с.проход}: ${с.доляНепустых}% / ${с.яркость} (${с.откуда})${с.причина ? ' — ' + с.причина : ''}`));
  return строки;
}

/** Дождаться, что счётчик кадров рендерера ВЫРОС. */
/**
 * Дождаться НАСТОЯЩЕГО кадра. [переписано в 10.19.19]
 *
 * ПОВОД — снимок Андрея Вячеславовича: «Кадр не получен за 8 с (движок webgpu).
 * Слой снят». А на экране при этом прекрасный объёмный рендер, то есть машина
 * работает. Разбор показал ГОНКУ В МОИХ ЖЕ ВОРОТАХ:
 *
 *   1. `enable()` запрашивает отрисовку, цикл рисует кадр;
 *   2. дальше `whenReady()` ждёт GPU;
 *   3. только ПОТОМ я снимал счётчик кадров и ждал, когда он ВЫРАСТЕТ.
 *
 * На быстрой видеокарте кадр успевает случиться на шаге 1–2, а сцена статична,
 * новых запросов на отрисовку нет — и счётчик больше не растёт НИКОГДА. Ждать
 * его дальше бессмысленно: через 8 с я объявлял исправный рендер сломанным и
 * снимал слой. У меня в песочнице программная отрисовка медленная, кадр
 * приходил уже во время ожидания, и я этого не видел ни разу.
 *
 * Теперь: сначала ЗАПРОСИТЬ кадр, потом ждать; успехом считается и рост
 * счётчика, и «кадры уже есть». А при неудаче спрашивается ПРИЧИНА ПРОСТОЯ у
 * самого рендерера — «за 8 с ничего не случилось» это не объяснение.
 */
function ждатьКадр(cr, ждатьМс = 8000) {
  return new Promise((resolve) => {
    // Запрос кадра ДО снятия счётчика: иначе ждём событие, которое уже прошло.
    try { cr.forceRender?.(); } catch (e) { log.debug('[Кино/кадр]', e.message); }
    const было = cr.getMetrics?.()?.totalFrames || 0;
    const t0 = Date.now();
    const тик = () => {
      const м = cr.getMetrics?.() || {};
      const стало = м.totalFrames || 0;
      if (стало > было) return resolve(стало - было);
      // Кадры уже нарисованы до нашего ожидания — это тоже успех, а не провал.
      if (стало > 0 && Date.now() - t0 > 600) return resolve(стало);
      if (Date.now() - t0 > ждатьМс) return resolve(0);
      setTimeout(тик, 200);
    };
    тик();
  });
}

/** Снять слой с экрана: он не должен закрывать нормальную картинку окна. */
function снятьСлой(cr) {
  try {
    cr.disable?.();
    const холст = cr.getCanvas?.();
    if (холст) холст.style.display = 'none';
  } catch (e) { log.debug('[Кино/снять]', e.message); }
}

/**
 * Включить кинематографический рендеринг честно.
 *
 * @param {{качество?: string, освещение?: string, перенос?: string, ao?: boolean, тени?: boolean,
 *          прогресс?: (доля: number, подпись?: string) => void, тихо?: boolean}} нстр
 * @returns {Promise<{ok: boolean, причина?: string, движок?: string, кадр?: number[], замер?: object}>}
 */
export async function включитьКино(нстр = {}) {
  // 10.19.17. Плашка замера пришла врачу ДВАЖДЫ — потому что включение можно
  // запустить дважды: два входа (кнопка «Рендер» и «Кино вкл/выкл»), двойной
  // щелчок, автоприменение настроек. Каждый заход доходил до замера и печатал
  // свою плашку. Один и тот же кадр, дважды объявленный непригодным, выглядит
  // как две разные поломки. Пока идёт включение, второй заход получает ТОТ ЖЕ
  // результат, а не запускает второй конвейер.
  if (_включениеВРаботе) return _включениеВРаботе;
  _включениеВРаботе = _включитьКиноОдин(нстр).finally(() => { _включениеВРаботе = null; });
  return _включениеВРаботе;
}

let _включениеВРаботе = null;

async function _включитьКиноОдин(нстр = {}) {
  const прогресс = typeof нстр.прогресс === 'function' ? нстр.прогресс : () => {};
  const сказать = (текст, вид = 'info', мс = 4000) => { if (!нстр.тихо) showToast(текст, вид, мс); };

  const { рендерер: cr, причина } = ensureCinematicRenderer();
  if (!cr) {
    прогресс(0);
    if (причина === 'нет-3d') сказать('Кинематографический рендеринг идёт в объёмном окне, а его в раскладке нет. Включите раскладку с 3D (MPR+3D) и повторите.', 'warning', 6000);
    else if (причина === 'нет-движка') сказать('Сначала откройте исследование — рендерить пока нечего.', 'warning', 4000);
    else сказать('Кинематографический рендеринг не поднялся: ' + причина, 'error', 7000);
    return { ok: false, причина: причина || 'рендерер не создан' };
  }

  прогресс(0.1, 'готовлю');
  const пиксели = _применитьНастройки(cr, нстр, сказать);

  // [10.19.19] Выбор движка из панели: «Только WebGL2» должен действовать и при
  // обычном включении, а не только в момент переключения списка. Иначе врач
  // выбрал WebGL2, перезапустил программу — и снова получил WebGPU без кадра.
  try {
    const выбран = localStorage.getItem('mikhmel.cinematicEngine');
    if (выбран === 'webgl2' && cr.getBackendName?.() !== 'webgl2') {
      await cr.перейтиНаWebGL2?.('выбор сохранён в настройках');
    }
  } catch (e) { log.debug('[Кино/движок]', e.message); }

  прогресс(0.35, 'включаю');
  const включён = cr.isEnabled() ? { success: true } : cr.enable();
  if (!включён.success) {
    прогресс(0);
    сказать('Рендер не включился: ' + включён.error, 'error', 7000);
    return { ok: false, причина: включён.error };
  }

  прогресс(0.55, 'GPU');
  const готов = await cr.whenReady();
  if (!готов.success) {
    прогресс(0);
    снятьСлой(cr);
    сказать('GPU для рендера не поднялся: ' + готов.error, 'error', 8000);
    return { ok: false, причина: готов.error };
  }

  прогресс(0.8, 'кадр');
  const новых = await ждатьКадр(cr, 8000);
  if (!новых) {
    прогресс(0);
    снятьСлой(cr);
    const простой = (() => { try { return cr.причинаПростоя?.(); } catch (e) { return null; } })();
    const почему = простой?.причина ? ` Причина: ${простой.причина}.` : '';
    сказать(`Кадр не получен за 8 с (движок ${cr.getBackendName?.() || '?'}).${почему}`
      + ' Слой снят — показывается обычный объёмный рендер. Отчёт WebGPU собирается сам — он появится в консоли (F12).', 'error', 12000);
    log.warn('[Кино] кадр не получен;', JSON.stringify(простой || {}));
    // [10.19.20] Кадра нет на WebGPU — диагностика запускается САМА и печатает
    // отчёт по проходам в консоль. Именно этого не хватало трижды: плашка
    // говорила «кадр не получен», а ЧТО невалидно — не говорил никто.
    if (cr.getBackendName?.() === 'webgpu') {
      import('../rendering/webgpuDiag.js')
        .then((м) => м.диагностикаWebGPU({ рендерер: cr }))
        .then((итог) => { console.log(итог.текст); })
        .catch((e) => log.debug('[Кино/отчётWebGPU]', e.message));
    }
    return { ok: false, причина: 'кадр не получен' + (простой?.причина ? ': ' + простой.причина : ''), простой };
  }

  // ── ЗАМЕР КАДРА. Ради него весь этот модуль и появился.
  const замер = await проверитьКадрТвёрдо(cr);
  if (!замер.пригоден) {
    прогресс(0);
    // Сначала диагностика — пока слой ещё на экране и его можно мерить.
    // Числа по проходам уходят в консоль: по ним видно, ГДЕ ломается.
    try { await диагностикаКонвейера(cr); } catch (e) { log.debug('[Кино/диагностика]', e.message); }
    снятьСлой(cr);
    сказать('Кинематографический слой дал непригодный кадр и снят с экрана. Замер: ' + замер.причина
      + '. Показывается обычный объёмный рендер — он в порядке.', 'error', 12000);
    log.warn('[Кино] слой снят по замеру кадра:', замер.причина);
    return { ok: false, причина: 'кадр непригоден: ' + замер.причина, замер };
  }

  const m = cr.getMetrics?.() || {};
  const [ш, в] = m.outputResolution || [0, 0];
  прогресс(1);
  сказать(`Рендер идёт: ${cr.getBackendName?.() || '?'}, ${ш}×${в}, ${Math.round(m.avgFPS || 0)} к/с`, 'success', 5000);

  const длинная = Math.max(ш, в);
  if (длинная > 0 && пиксели > длинная + 1) {
    сказать(`Качество «${пиксели}px» ограничено размером окна: кадр ${ш}×${в}. Считать кадр крупнее окна программа пока не умеет — это ломало картинку.`, 'info', 8000);
  }

  return { ok: true, движок: cr.getBackendName?.() || null, кадр: [ш, в], замер };
}

/**
 * [12.0.51] Настройки панели — перенос, качество, AO/тени, свет — в живой
 * рендерер. Раньше это делалось только внутри «Рендер», и смена «Качества»,
 * «Освещения», «Переноса» или AO при ИДУЩЕМ рендере не делала ничего, пока
 * врач снова не нажмёт «Рендер». Теперь тот же код зовётся и при смене
 * настроек (ui/featureBindings.js) — один путь, а не два.
 */
function _применитьНастройки(cr, нстр, сказать, части = ЧАСТИ_ВСЕ) {
  // Перенос: КТ — пресетом, МРТ — по гистограмме серии. Только когда сменили
  // сам перенос (или при «Рендер»): иначе смена света затирала бы правки
  // редактора передаточной функции.
  const перенос = нстр.перенос || 'ct_default';
  if (!части.has('перенос')) { /* перенос не трогаем */ } else if (ПЕРЕНОС_МР[перенос]) {
    const р = применитьМРПеренос(перенос);
    if (р.ok) сказать(`Перенос «${ПЕРЕНОС_МР[перенос]}»: окно по гистограмме серии ${Math.round(р.окно.низ)}…${Math.round(р.окно.верх)}`, 'info', 4000);
    else сказать(`Перенос «${ПЕРЕНОС_МР[перенос]}» не применён: ${р.причина}`, 'warning', 6000);
  } else if (ПЕРЕНОС_КТ[перенос] && !set3DPreset(ПЕРЕНОС_КТ[перенос])) {
    сказать(`Перенос «${перенос}»: объёмное окно не приняло пресет ${ПЕРЕНОС_КТ[перенос]}`, 'warning', 4000);
  }

  const пиксели = КАЧЕСТВО_В_ПИКСЕЛИ[нстр.качество] || 1024;
  if (части.has('качество')) { try { cr.setResolutionCap(пиксели); } catch (e) { log.debug('[Кино/качество]', e.message); } }
  // 10.19.17: затенение и тени — только при НАСТОЯЩЕМ буфере глубины. Раньше
  // сюда шло `нстр.ao !== false`, и SSAO включался всегда, считая окклюзию по
  // яркости кадра. Замер: яркость слоя 15,7 → 4,4 от одного этого прохода.
  const глубинаЕсть = typeof cr.hasDepth === 'function' ? cr.hasDepth() : false;
  if (части.has('глубина')) {
    try {
      cr.setParams({
        ssao: { enabled: !!нстр.ao && глубинаЕсть },
        volumetric: { shadows: !!нстр.тени && глубинаЕсть },
      });
    } catch (e) { log.debug('[Кино/параметры]', e.message); }
    if (!глубинаЕсть && (нстр.ao || нстр.тени)) {
      сказать('Затенение и тени требуют буфера глубины — объёмное окно его не отдаёт. '
        + 'Эти эффекты не включены; остальной конвейер работает.', 'info', 7000);
    }
  }
  if (части.has('свет')) { try { cr.setLights(СВЕТ[нстр.освещение] || СВЕТ.studio); } catch (e) { log.debug('[Кино/свет]', e.message); } }
  return пиксели;
}

const ЧАСТИ_ВСЕ = new Set(['перенос', 'качество', 'глубина', 'свет']);

/**
 * Применить к идущему рендеру одну часть настроек панели: 'перенос',
 * 'качество', 'глубина' (AO и тени) или 'свет'. Рендер не идёт — ничего:
 * настройки возьмёт следующий «Рендер».
 */
export function применитьНастройкиКино(нстр = {}, часть = null) {
  const cr = getCinematicRenderer();
  if (!cr || cr.isDisposed?.() || !cr.isEnabled?.()) return { ok: false, причина: 'рендер не идёт' };
  const сказать = (текст, вид = 'info', мс = 4000) => { if (!нстр.тихо) showToast(текст, вид, мс); };
  _применитьНастройки(cr, нстр, сказать, часть ? new Set([часть]) : ЧАСТИ_ВСЕ);
  try { cr.forceRender?.(); } catch (e) { log.debug('[Кино/настройки]', e.message); }
  return { ok: true };
}

/** Выключить — и сказать правду о том, что было. */
export function выключитьКино({ тихо = false } = {}) {
  const cr = getCinematicRenderer();
  if (!cr || !cr.isEnabled()) {
    if (!тихо) showToast('Кинематографический рендеринг не запущен — выключать нечего', 'info', 2500);
    return { ok: true, было: false };
  }
  cr.disable();
  const холст = cr.getCanvas?.();
  if (холст) холст.style.display = 'none';
  if (!тихо) showToast('Кинематографический рендеринг выключен', 'info', 2000);
  return { ok: true, было: true };
}

/** Состояние — для кнопок-переключателей. */
export function состояниеКино() {
  const cr = getCinematicRenderer();
  return { есть: !!cr, включён: !!cr?.isEnabled?.(), движок: cr?.getBackendName?.() || null };
}

/**
 * Переключатель для кнопок «Кино вкл/выкл». Настройки берутся из правой панели —
 * чтобы оба входа делали ровно одно и то же.
 */
export async function переключитьКино(нстр = {}) {
  const было = состояниеКино();
  if (было.включён) return { ...выключитьКино(нстр), включено: false };
  const итог = await включитьКино(нстр);
  return { ...итог, включено: !!итог.ok };
}

export default { включитьКино, выключитьКино, переключитьКино, состояниеКино, применитьНастройкиКино, проверитьКадр, проверитьКадрТвёрдо, диагностикаКонвейера, КАЧЕСТВО_В_ПИКСЕЛИ, СВЕТ };
